import { useState } from "react"
import { stringify } from "querystringify"
import { useNavigate } from "react-router-dom"

import { Analytics } from "utils/analytics"
import { saveUserDataInLocalStorage } from "modules/Booking/FormPage/common/index"
import { FunnelStorage } from "modules/FunnelAnalytics"
import { UserAccountData, ErrorResponse } from "utils/interfaces"
import { useBookingContext } from "modules/Booking/BookingContext"
import { BannerStorage } from "modules/discount/BannerStorage"
import { SESSION_TYPE } from "modules/Booking/common/constants"
import { ServiceType } from "utils/helpers"
import {
    fetchCreateBookSession,
    fetchCreateDeposit,
    fetchCreateTimeRequest,
} from "modules/Booking/FormPage/common/api"
import { sentryBookSessionError } from "modules/Booking/common/utils"
import { putOrderAutoPay } from "modules/BookingCommon/common/api"
import {
    createdTimeRequestLoader,
    createdBookingLoader,
    createdDepositLoader,
} from "modules/BookingCommon/common/queries"
import { Pages, INSTANT_WIDGET } from "modules/BookingCommon/common/constants"
import { ERRORS_FOR_DESELECT_DISCOUNT } from "modules/Booking/FormPage/common/hooks/common/constants"
import { useOnlyFreeSessions } from "pages/FreePsysPage/useOnlyFreeSessions"
import { basePaymentUrl } from "utils/helpers/paymentUrl"
import { nonNullable } from "utils/nonNullable"
import { useSearchParams } from "utils/customHooks"
import { SentryWrapper } from "utils/ThirdParty/Sentry/SentryWrapper"
import { formatToyLd } from "utils/datetime-format-utils"
import { APP_STATUSES } from "utils/AppStatusContext"
import * as utils from "utils/utils"

interface RunPutOrderAutoPay {
    card_external_id: string
    searchParams: URLSearchParams | Map<string, string | null>
    order_id: string | number
    psyHashId: string
    booking_hash?: string
    time_request_hash?: string
    deposit_hash?: string
    order_hash: string
}

interface GetPaymentUrl {
    hash_id: string
    searchParams: URLSearchParams | Map<string, string | null>
    payment_id?: string
    booking_hash?: string
    time_request_hash?: string
    deposit_hash?: string
    order_hash?: string
}

const getPaymentUrl = (data: GetPaymentUrl) => {
    const returnUrl = "/booking"
    const params = {
        ...basePaymentUrl(data.searchParams, data.hash_id, !!data.payment_id),
        ...(data.payment_id && { payment_id: data.payment_id }),
        ...(data.booking_hash && { booking_hash: data.booking_hash }),
        ...(data.time_request_hash && { time_request_hash: data.time_request_hash }),
        ...(data.deposit_hash && { deposit_hash: data.deposit_hash }),
        ...(data.order_hash && { order_hash: data.order_hash }),
    }

    return `${returnUrl}${stringify(params, true)}`
}

export const useBookingApi = (userData: UserAccountData) => {
    const navigate = useNavigate()
    const onlyFree = useOnlyFreeSessions()
    const searchParams = useSearchParams()
    const [
        {
            selectedTime,
            psy,
            slotId,
            selectedDiscount,
            currency,
            sessionType,
            requestTimesSelectors,
            selectedService,
            selectDeposit,
            hash,
        },
        { setErrorMessage, setSelectedDiscount, toggleIsLoading, setHash },
    ] = useBookingContext()

    const [isLoading, setIsLoading] = useState(false)
    const { data: bookingDetails } = createdBookingLoader.useGet(
        sessionType === SESSION_TYPE.DEFAULT ? hash : undefined
    )
    createdTimeRequestLoader.useGet(sessionType === SESSION_TYPE.TIME_REQUEST ? hash : undefined)
    createdDepositLoader.useGet(sessionType === SESSION_TYPE.DEPOSIT ? hash : undefined)

    const setErrors = (error: ErrorResponse) => {
        const message = error?.message || error?.error || ""
        if (ERRORS_FOR_DESELECT_DISCOUNT.includes(message)) {
            setErrorMessage(message)
            setSelectedDiscount(undefined)
        }

        setErrorMessage(message)
    }

    const runPutOrderAutoPay = (props: RunPutOrderAutoPay) => {
        toggleIsLoading(true)
        putOrderAutoPay(props.order_id, props.card_external_id)
            .then((payDetails) => {
                navigate(
                    getPaymentUrl({
                        hash_id: props.psyHashId,
                        booking_hash: props.booking_hash,
                        searchParams: props.searchParams,
                        payment_id: payDetails.payment_id,
                        time_request_hash: props.time_request_hash,
                        deposit_hash: props.deposit_hash,
                        order_hash: props.order_hash,
                    }),
                    {
                        state: {
                            bookingPage: Pages.PAYMENT,
                            reloadPage: "1",
                        },
                        replace: true,
                    }
                )
            })
            .catch((error) => {
                SentryWrapper.getInstance().captureException(error, {
                    extra: { order_id: props.order_id },
                })
                window.location.href = getPaymentUrl({
                    hash_id: props.psyHashId,
                    booking_hash: props.booking_hash,
                    searchParams: props.searchParams,
                    time_request_hash: props.time_request_hash,
                    deposit_hash: props.deposit_hash,
                    order_hash: props.order_hash,
                })
            })
            .finally(() => {
                toggleIsLoading(false)
            })
    }

    const createBookSession = async (card_external_id?: string, isAutoPay?: boolean) => {
        if (!selectedService) {
            SentryWrapper.getInstance().captureException(
                new Error("BookingDetails: selectedService is undefined"),
                { tags: { psy: psy.hash_id } }
            )
            return
        }

        if (!slotId) {
            SentryWrapper.getInstance().captureException(
                new Error("BookingDetails: slotId is undefined"),
                { tags: { psy: psy.hash_id } }
            )
            return
        }

        try {
            setIsLoading(true)
            const data = await fetchCreateBookSession(
                {
                    date: utils.formatUtcDateIso(selectedTime),
                    time: utils.formatUtcTimeIso(selectedTime),
                    client_data: {
                        name: userData.name,
                        email: userData.email,
                        phone: userData.phone,
                    },
                    discount: selectedDiscount,
                    service_type: new ServiceType(selectedService),
                    currency,
                    psy_hash_id: psy.hash_id,
                    slot_id: slotId,
                    created_by_psychologist: false,
                },
                onlyFree
            )
            FunnelStorage.getInstance().cleanStorage()
            setHash(data.hash)
            createdBookingLoader.onSuccess((bookingDetails) => {
                if (card_external_id && isAutoPay) {
                    runPutOrderAutoPay({
                        card_external_id,
                        searchParams,
                        psyHashId: bookingDetails.psy.hash_id,
                        order_id: bookingDetails.order_id,
                        booking_hash: bookingDetails.booking_hash,
                        order_hash: bookingDetails.order_hash,
                    })
                } else {
                    if (searchParams.get("from") === APP_STATUSES.client_account) {
                        Analytics.lk_pay_confirmed()
                    }
                    const url = new URL(window.location.toString())
                    url.searchParams.set("booking_hash", bookingDetails.booking_hash)
                    url.searchParams.set("order_hash", bookingDetails.order_hash)
                    url.searchParams.set(INSTANT_WIDGET, "1")
                    navigate(url.pathname + url.search, {
                        state: {
                            bookingPage: Pages.PAYMENT,
                        },
                        replace: true,
                    })
                    BannerStorage.reset()
                }
            })
        } catch (err) {
            const e = err as ErrorResponse
            setErrors(e?.response || e)
            sentryBookSessionError(onlyFree, e)
        } finally {
            setIsLoading(false)
        }
    }

    const createTimeRequest = async (card_external_id?: string, isAutoPay?: boolean) => {
        if (!selectedService) {
            SentryWrapper.getInstance().captureException(
                new Error("useBookingApi: selectedService for TimeRequest is undefined"),
                { tags: { psy: psy.hash_id } }
            )
            return
        }
        try {
            const data = await fetchCreateTimeRequest({
                client_data: {
                    name: userData.name,
                    email: userData.email,
                    phone: userData.phone,
                },
                discount: selectedDiscount || null,
                service_type: selectedService.service_type_id,
                currency,
                psychologist: psy.hash_id,
                requested_session_datetimes: requestTimesSelectors
                    .map(({ date, time }) => {
                        if (date && time) {
                            const localeDate = new Date(`${date} ${time}`)
                            const timeZoneOffset = -localeDate.getTimezoneOffset() * 60 * 1000
                            const dateWithUtc = new Date(localeDate.getTime() - timeZoneOffset)
                            const formatDate = formatToyLd(dateWithUtc)
                            const getFormat = utils.formatUtcTimeIso(localeDate)
                            return {
                                date: formatDate,
                                time: getFormat,
                            }
                        }
                    })
                    .filter(nonNullable),
            })
            FunnelStorage.getInstance().cleanStorage()
            setHash(data.hash)
            createdTimeRequestLoader.onSuccess(() => {
                if (card_external_id && isAutoPay) {
                    runPutOrderAutoPay({
                        card_external_id,
                        searchParams,
                        psyHashId: psy.hash_id,
                        order_id: data.order_id,
                        time_request_hash: data.hash,
                        order_hash: data.order_hash,
                    })
                } else {
                    const url = new URL(window.location.toString())
                    url.searchParams.set("time_request_hash", data.hash)
                    url.searchParams.set("order_hash", data.order_hash)
                    url.searchParams.set(INSTANT_WIDGET, "1")
                    navigate(url.pathname + url.search, {
                        state: {
                            bookingPage: Pages.PAYMENT,
                        },
                        replace: true,
                    })
                    BannerStorage.reset()
                }
            })
        } catch (err: unknown) {
            const e = err as ErrorResponse
            if (e?.response) setErrors(e?.response || e)
            const e1 = err as { response: { data: ErrorResponse } }
            if (e1?.response?.data) setErrors(e1?.response?.data)
        } finally {
            setIsLoading(false)
        }
    }

    const createDeposit = async (card_external_id?: string, isAutoPay?: boolean) => {
        if (!selectedService) {
            SentryWrapper.getInstance().captureException(
                new Error("useBookingApi: selectedService for Deposit is undefined"),
                { tags: { psy: psy.hash_id } }
            )
            return
        }

        if (!selectDeposit) {
            SentryWrapper.getInstance().captureException(
                new Error("useBookingApi: selectDeposit for Deposit is undefined"),
                { tags: { psy: psy.hash_id } }
            )
            return
        }

        setIsLoading(true)

        try {
            const data = await fetchCreateDeposit({
                date: utils.formatUtcDateIso(selectedTime),
                time: utils.formatUtcTimeIso(selectedTime),
                created_by_psychologist: false,
                deposit_configuration: selectDeposit.id,
                client_data: {
                    name: userData.name,
                    email: userData.email,
                    phone: userData.phone,
                },
                psy_hash_id: psy.hash_id,
                service_type: new ServiceType(selectedService),
                slot_id: slotId,
                currency: currency,
            })
            setHash(data.deposits[0].hash)
            createdDepositLoader.onSuccess(() => {
                if (card_external_id && isAutoPay) {
                    toggleIsLoading(true)
                    runPutOrderAutoPay({
                        card_external_id,
                        searchParams,
                        psyHashId: psy.hash_id,
                        order_id: data.id,
                        deposit_hash: data.deposits[0].hash,
                        order_hash: data.hash,
                    })
                } else {
                    const url = new URL(window.location.toString())
                    url.searchParams.set("deposit_hash", data.deposits[0].hash)
                    url.searchParams.set("order_hash", data.hash)
                    url.searchParams.set(INSTANT_WIDGET, "1")
                    navigate(url.pathname + url.search, {
                        state: {
                            bookingPage: Pages.PAYMENT,
                        },
                        replace: true,
                    })
                    BannerStorage.reset()
                }
            })
        } catch (err) {
            const e = err as ErrorResponse
            setErrors(e?.response || e)
        } finally {
            setIsLoading(false)
        }
    }

    const handleClickButton = (card_external_id: string | undefined, isAutoPay?: boolean) => {
        const isBooked = Boolean(hash)
        if (isBooked && !!card_external_id) {
            const url = new URL(window.location.toString())
            navigate(url.pathname + url.search, {
                state: { bookingPage: Pages.PAYMENT },
                replace: true,
            })
            return
        }

        if (isBooked && card_external_id && bookingDetails && isAutoPay) {
            setErrorMessage(undefined)
            toggleIsLoading(true)
            runPutOrderAutoPay({
                card_external_id,
                searchParams,
                psyHashId: bookingDetails.psy.hash_id,
                order_id: bookingDetails.order_id,
                booking_hash: bookingDetails.booking_hash,
                order_hash: bookingDetails.order_hash,
            })
            return
        }

        if (!userData.name || !userData.email || !userData.phone) {
            SentryWrapper.getInstance().captureException(
                new Error("useBookingApi: Missing user data"),
                { tags: { psy: psy.hash_id } }
            )
            return
        }

        Analytics.session_details_confirmed()
        saveUserDataInLocalStorage(userData.email, userData.name, userData.phone)
        setIsLoading(true)
        switch (sessionType) {
            case SESSION_TYPE.DEFAULT:
                void createBookSession(card_external_id, isAutoPay)
                break
            case SESSION_TYPE.DEPOSIT:
                void createDeposit(card_external_id, isAutoPay)
                break
            case SESSION_TYPE.TIME_REQUEST:
                void createTimeRequest(card_external_id, isAutoPay)
                break
        }
    }

    return {
        isLoading,
        createBookSession,
        handleClickButton,
    }
}
